/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionPublishEvent';

const ActionPublishEventManager: ActionManagerFn<'ActionPublishEvent'> = (
  platform: Platform,
): ActionManager<'ActionPublishEvent'> => {
  return {
    ...baseActionManager(platform, 'ActionPublishEvent'),
    defaultValue,
    isChildAllowed: (page, item, childType) => childType === 'ActionSetParam',
    defaultChildren: ['ActionSetParam', 'ActionSetParam'],
    props: {
      ...baseActionProps,
      context: {
        label: 'Context',
        type: 'expression',
        returnType: async () => 'string',
        placeholder: 'e.g. Order',
        helpText: 'Specifiy the Context to which the event to be published.',
      },
      contextId: {
        label: 'Context ID',
        type: 'expression',
        returnType: async () => 'string',
        placeholder: 'e.g. #{OrderDS.orderId}',
        helpText: 'Specifiy the Context ID for the event to be published.',
      },
    },
    name: 'Publish Event',
    getDisplayText: (item) => item.props.context.value,
    description: `**Overview**<br/>
The Publish Event action is a messaging component designed to facilitate event-driven architecture within the application. It acts as a broadcaster in a "Publisher/Subscriber" model, allowing the system to announce that a specific business event has occurred (like "OrderPlaced" or "UserVerified") without needing to know which specific functions or components are listening.<br/><br/>
This action works in tandem with the Event Subscription component. While "Publish Event" broadcasts the signal, "Event Subscription" listens for it and triggers reaction logic. This decoupling allows developers to build modular, scalable applications where independent components communicate through standard events rather than hard-coded direct calls. It uses ActionSetParam child components to define the payload (data) attached to the event.

**Key Features**<br/>
* **Event-Driven Workflow:** Enables indirect communication between different parts of the system, allowing multiple independent actions to react to a single trigger.
* **Payload Construction:** Strictly enforces the use of ActionSetParam child components to define the data object passed along with the event (e.g., passing the { orderId: 123, total: 500 } with the "OrderPlaced" event).
* **Contextual Scoping:** Uses context and contextId properties to categorize events, ensuring listeners can filter and react only to relevant signals (e.g., distinguishing between "Order" events and "User" events).
* **Dynamic Definition:** Both context properties support expressions, allowing the event topic and ID to be resolved at runtime based on the data being processed.

**Properties**<br/>
* **Context (Required):** An expression property returning a string.
  * *Function:* Defines the category or "Topic" of the event (e.g., Order, Inventory, Auth). This helps organize events into logical groups.
* **Context ID (Required):** An expression property returning a string.
  * *Function:* Identifies the specific entity instance associated with the event (e.g., a specific Order ID or User ID).

**Use Cases**<br/>
* **Process Orchestration:** A "Submit Order" button publishes an Order.Submitted event. Three separate subscriptions listen for this: one clears the shopping cart, another sends a confirmation email, and a third deducts stock from inventory.
* **Cross-Component Sync:** A "Refresh" button in a sidebar publishes a Global.RefreshData event; multiple independent data grids on the main page listen for this event and reload their stores simultaneously.
* **Loose Coupling:** Allowing a child component to signal a state change to its parent without passing a callback function down the hierarchy.`,
  };
};

export default ActionPublishEventManager;
